<script setup lang="ts">
import type { ISelectListItem } from '@/components/forms/ISelectListItem'
import { computed, type PropType } from 'vue'
import { FORM_SELECT_VALUE_TYPE } from '@/components/forms/form.enum'
import type { ILanguage } from '@/utils/sharedData/languages'

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  list: {
    type: Array<ISelectListItem | string>,
    required: true
  },
  default: {
    type: Object as PropType<ISelectListItem | null>,
    require: false
  },
  placeholder: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  modelValue: {
    required: true
  },
  valueType: {
    type: String as PropType<FORM_SELECT_VALUE_TYPE>,
    require: true
  },
  disabled: {
    type: Boolean,
    require: false
  },
  isContactus: {
    type: Boolean,
    require: false,
    default: false
  },
  isLightMode: {
    type: Boolean,
    required: false,
    default: false
  },
  isLightGray: {
    type: Boolean,
    required: false,
    default: true
  },
  isSmallLabel: {
    type: Boolean,
    required: false,
    default: false
  },
  isBoldLabel: {
    type: Boolean,
    required: false,
    default: false
  },
  isSettings: {
    type: Boolean,
    required: false,
    default: false
  },
  customClass: {
    type: String,
    required: false,
    default: null
  },
  displayName: {
    type: String,
    required: false,
    default: null
  },
  isAssessmentUsingAi: {
    type: Boolean,
    required: false,
    default: false
  },
  labelClass: {
    type: String,
    required: false,
    default: ''
  },
  showLable: {
    type: Boolean,
    default: true,
    required: false
  },
  getSelectedByItem: {
    type: Boolean,
    default: false,
    required: false
  }
})

/**
 *
 * @param item String | ISelectListItem
 * @returns item
 */
function getItemDisplay(item: String | ISelectListItem) {
  if (props.displayName && props.displayName == FORM_SELECT_VALUE_TYPE.NAME) {
    return (item as ISelectListItem).name
  }

  if (typeof item == 'string') {
    return item
  }

  if ((item as ISelectListItem).displayName) return (item as ISelectListItem).displayName

  if ((item as ISelectListItem).type) return (item as ISelectListItem).type
  if ((item as ISelectListItem).name) return (item as ISelectListItem).name

  return item
}

/**
 *
 * @param item String | ISelectListItem
 * @param index number
 * @returns item
 */
function getItemValue(item: String | ISelectListItem, index: number) {
  if (props.valueType === FORM_SELECT_VALUE_TYPE.INDEX) {
    return index
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.NAME) {
    return (item as ISelectListItem).displayName
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.VALUE) {
    return (item as ISelectListItem).value
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.TYPE) {
    return (item as ISelectListItem).type
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.LANGUAGE) {
    return (item as ILanguage).language
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.ROLE_NAME) {
    return item as ISelectListItem
  }

  return item
}

/**
 *
 * @param item ISelectListItem | string
 * @param index number
 * @returns boolean
 */
function getSelectedItem(item: ISelectListItem | string | any, index: number) {
  if (props.valueType === FORM_SELECT_VALUE_TYPE.INDEX) {
    return index === props.modelValue
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.NAME) {
    if (props.getSelectedByItem) {
      return (item as ISelectListItem) === props.modelValue
    }
    return (item as ISelectListItem).displayName === props.modelValue
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.VALUE) {
    return (item as ISelectListItem).value === props.modelValue
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.TYPE) {
    return (item as ISelectListItem).type === props.modelValue
  }

  if (props.valueType === FORM_SELECT_VALUE_TYPE.LANGUAGE) {
    return (item as ILanguage).language === props.modelValue
  }

  if (props.default) {
    return item == (props.default as ISelectListItem)
  }
  return item == props.modelValue
}

const inputClasses = computed(() => {
  switch (true) {
    case props.isContactus:
      return 'rounded-none border-b px-1'
    case props.isLightGray:
      return `bg-neutral-50 dark:bg-[#17191F] rounded-md px-4`
    case props.isLightMode:
      return 'bg-neutral-50 rounded-md px-4 text-black'
    default:
      return `rounded-md px-4`
  }
})
</script>

<template>
  <div>
    <div v-if="showLable" class="flex items-center justify-between">
      <label
        :for="props.name || `${props.label + props.placeholder}`"
        class="my-2 block text-base"
        :class="[
          props.labelClass,
          props.isSmallLabel ? 'text-sm' : '',
          props.isSettings ? 'text-xs font-semibold md:text-sm lg:text-base' : '',
          props.isBoldLabel ? 'font-medium' : ''
        ]"
        >{{ props.label }}
      </label>
      <slot />
    </div>
    <div class="relative">
      <select
        class="section-primary p-small block w-full border border-text-tertiary py-2"
        :class="[inputClasses, customClass]"
        v-bind="$attrs"
        :id="props.name || `${props.label + props.placeholder}`"
        :placeholder="props.placeholder"
        @change="(event: Event) => $emit('update:modelValue', (event.target as HTMLSelectElement).value)"
        :disabled="props.isAssessmentUsingAi"
      >
        <!-- Null as default -->
        <option v-if="props.default === null" :selected="true" :disabled="true">
          Please Select
        </option>
        <!-- Specified Default, or first item -->
        <option
          v-for="(item, index) in list"
          :selected="getSelectedItem(item, index)"
          :value="getItemValue(item, index)"
          :key="index"
        >
          {{ getItemDisplay(item) }}
        </option>
      </select>

      <div
        :class="[
          'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3',
          { hidden: !props.error }
        ]"
      >
        <FontAwesomeIcon icon="fa-circle-xmark" class="error h-5 w-5" aria-hidden="true" />
      </div>
    </div>
    <p :class="['p-xsmall error', { hidden: !props.error }]">
      {{ props.error }}
    </p>
  </div>
</template>

<style>
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M8.35355 4.06066C8.15829 3.8654 7.84171 3.8654 7.64645 4.06066L5.35355 6.35355C5.15829 6.54882 4.84171 6.54882 4.64645 6.35355C4.45118 6.15829 4.45118 5.84171 4.64645 5.64645L6.93934 3.35356C7.52513 2.76777 8.47487 2.76777 9.06066 3.35355L11.3536 5.64645C11.5488 5.84171 11.5488 6.15829 11.3536 6.35355C11.1583 6.54882 10.8417 6.54882 10.6464 6.35355L8.35355 4.06066Z' fill='%236b7280'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M8.35355 11.9393C8.15829 12.1346 7.84171 12.1346 7.64645 11.9393L5.35355 9.64645C5.15829 9.45119 4.84171 9.45119 4.64645 9.64645C4.45118 9.84171 4.45118 10.1583 4.64645 10.3536L6.93934 12.6464C7.52513 13.2322 8.47487 13.2322 9.06066 12.6464L11.3536 10.3536C11.5488 10.1583 11.5488 9.84171 11.3536 9.64645C11.1583 9.45119 10.8417 9.45119 10.6464 9.64645L8.35355 11.9393Z' fill='%236b7280'/%3E%3C/svg%3E%0A");
  background-size: 1.25em 1.25em;
  background-position: right 0.625rem center;
  background-repeat: no-repeat;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
</style>
